import Glide, { Swipe } from '@glidejs/glide/dist/glide.modular.esm'

export default function InitProcessGraphic() {
    document.querySelectorAll('[data-process-graphic-slider]').forEach((sliderEl) => {
        const slides = sliderEl.querySelectorAll('.glide__slide')

        // nothing to slide (e.g. collection still empty while editing) — skip
        if (!slides.length) {
            return
        }

        const prevButton = sliderEl.querySelector('[data-process-graphic-prev]')
        const nextButton = sliderEl.querySelector('[data-process-graphic-next]')
        const dotsList = sliderEl.querySelector('[data-process-graphic-dots]')

        // One dot per tile is only correct when perView is 1. With
        // `bound: true`, glide clamps its max index to wherever the last
        // slide still lines up flush with the track's right edge — the
        // same threshold "index + perView >= slides.length" already used
        // by updateArrows() below — so with e.g. 8 tiles at perView 6
        // there are only 3 reachable positions (index 0, 1, 2), not 8.
        // Building one dot per tile left 5 dead dots that never activated
        // and jumped to the same clamped position as dot 2 when clicked.
        // Dots are rebuilt whenever perView changes (mobile's perView is a
        // fraction of the viewport, recalculated on resize).
        let dots = []
        const renderDots = (perView) => {
            const maxIndex = Math.max(0, Math.ceil(slides.length - perView))
            const dotCount = maxIndex + 1

            dotsList.innerHTML = ''
            for (let i = 0; i < dotCount; i++) {
                const li = document.createElement('li')
                const button = document.createElement('button')
                button.type = 'button'
                button.className = 'process-graphic__dot'
                button.setAttribute('aria-label', `Slide ${i + 1}`)
                button.dataset.index = i
                li.appendChild(button)
                dotsList.appendChild(li)
            }
            dots = dotsList.querySelectorAll('.process-graphic__dot')
        }

        dotsList.addEventListener('click', (event) => {
            const button = event.target.closest('.process-graphic__dot')
            if (button && glide) {
                glide.go(`=${button.dataset.index}`)
            }
        })

        // 6 tiles are designed to fit a single row from 992px up. Below
        // that width it's always a slider instead of wrapping into more
        // rows — cards keep roughly a fixed size (matching their desktop
        // scale) rather than being stretched to fill exactly 1 or 2 slots,
        // so perView there is a fraction computed from the visible track
        // width: however many cards (plus a peek of the next one) actually
        // fit is what shows, ~1.5 on phones, more on tablets, recalculated
        // on resize. From 992px up it only becomes a slider once there are
        // MORE than 6 tiles (see MAX_TILES_PER_ROW below) — with 6 or
        // fewer it stays the plain wrapping/centered row (see
        // ProcessGraphic.scss's "&__row" and "&:not(.is-sliding)" rules).
        const MAX_TILES_PER_ROW = 6
        const hasOverflow = slides.length > MAX_TILES_PER_ROW
        const mobileQuery = window.matchMedia('(max-width: 991px)')
        const CARD_WIDTH = 220
        const GAP = 16
        const getPerView = () => {
            // desktop-slider mode (>6 tiles, 992px+): each tile is still
            // exactly 1/6 of the track (see ProcessGraphic.scss), so 6 is
            // always what's visible at once, however many extra tiles
            // exist beyond that slide in from the right
            if (!mobileQuery.matches) {
                return MAX_TILES_PER_ROW
            }

            const trackWidth = sliderEl.offsetWidth
            return Math.max(1, trackWidth / (CARD_WIDTH + GAP))
        }

        let glide = null

        const setActive = (index) => {
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index))
        }

        const updateArrows = () => {
            prevButton?.classList.toggle('is-disabled', glide.index === 0)
            nextButton?.classList.toggle('is-disabled', glide.index + glide.settings.perView >= slides.length)
        }

        const mount = () => {
            if (glide) {
                return
            }

            renderDots(getPerView())

            // no loop (client request, applies to every slider on the
            // site) — "slider" + bound:true stops firmly at the last
            // slide instead of "carousel" wrapping back to the start
            glide = new Glide(sliderEl, {
                type: 'slider',
                bound: true,
                // glide.js defaults "rewind" to true even for "slider"
                // type — without this it silently wraps around
                rewind: false,
                perView: getPerView(),
                gap: GAP,
                animationDuration: 400,
                // Neos.Neos:ContentCollection wraps the tiles in its own
                // ".neos-contentcollection" div — treat that as the slides
                // track directly instead of fighting the extra wrapper
                classes: { slides: 'neos-contentcollection' },
            })

            glide.on(['mount.after', 'run'], () => {
                setActive(glide.index)
                updateArrows()
            })
            glide.mount({ Swipe })
        }

        const destroy = () => {
            if (!glide) {
                return
            }

            glide.destroy()
            glide = null
        }

        prevButton.addEventListener('click', () => glide && glide.go('<'))
        nextButton.addEventListener('click', () => glide && glide.go('>'))

        const sync = () => {
            const isSliding = mobileQuery.matches || hasOverflow
            sliderEl.classList.toggle('is-sliding', isSliding)

            if (isSliding) {
                if (glide) {
                    // mobile's perView is a fraction of the viewport width —
                    // it can change on resize, which also changes how many
                    // reachable slide positions (dots) there are
                    const perView = getPerView()
                    renderDots(perView)
                    glide.update({ perView })
                    setActive(glide.index)
                    updateArrows()
                } else {
                    mount()
                }
            } else {
                destroy()
            }
        }

        sync()

        let resizeTimeout
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout)
            resizeTimeout = setTimeout(sync, 150)
        })
    })
}
